import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Users, Heart, Globe, Award } from 'lucide-react';

const About = () => {
  const { language, t } = useLanguage();

  const values = [
    {
      icon: Globe,
      title: t('about_value_1'),
      description: language === 'de'
        ? 'Wir feiern die Vielfalt und Einzigartigkeit beider Kulturen.'
        : 'Celebriamo la diversità e l\'unicità di entrambe le culture.',
    },
    {
      icon: Heart,
      title: t('about_value_2'),
      description: language === 'de'
        ? 'Gegenseitiger Respekt und echte Freundschaft bilden unser Fundament.'
        : 'Il rispetto reciproco e l\'amicizia genuina sono le nostre fondamenta.',
    },
    {
      icon: Award,
      title: t('about_value_3'),
      description: language === 'de'
        ? 'Wir ehren Traditionen und bleiben offen für neue Ideen.'
        : 'Onoriamo le tradizioni e restiamo aperti a nuove idee.',
    },
  ];

  const teamMembers = [
    {
      name: 'Aurelio Marrelli',
      role: language === 'de' ? 'Vorsitzender' : 'Presidente',
      initials: 'AM',
    },
    {
      name: 'Dr. Alessandro Bellardita',
      role: language === 'de' ? 'Stellv. Vorsitzender' : 'Vice Presidente',
      initials: 'AB',
    },
  ];

  return (
    <div className="overflow-hidden" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="year-badge mb-6 inline-block">{t('founded')} 2011</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6" data-testid="about-title">
              {t('about_title')}
            </h1>
            <p className="text-lg text-foreground/70 leading-relaxed">
              {t('about_intro')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
                {language === 'de' ? 'Was ist das Ziel von Amitalia e.V.?' : 'Qual è l\'obiettivo di Amitalia e.V.?'}
              </h2>
              <div className="text-foreground/70 leading-relaxed space-y-4">
                <p>
                  {language === 'de' 
                    ? 'Amitalia e. V. wurde gegründet, um die in Deutschland lebenden Italiener enger mit der italienischen und deutschen Kultur zu verbinden und einen lebendigen Austausch zwischen beiden Ländern zu fördern.'
                    : 'Amitalia e.V. è stata fondata per collegare più strettamente gli italiani che vivono in Germania con la cultura italiana e tedesca e per promuovere uno scambio vivace tra i due paesi.'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Der italienisch-deutsche Kulturverein wurde 2011 in Bruchsal gegründet, um das Verständnis für die italienische Kultur zu vertiefen und den Austausch zwischen Italien und Deutschland durch inspirierende Events und persönliche Begegnungen voranzubringen.'
                    : 'L\'associazione culturale italo-tedesca è stata fondata nel 2011 a Bruchsal per approfondire la comprensione della cultura italiana e promuovere lo scambio tra Italia e Germania attraverso eventi ispiranti e incontri personali.'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Bald wurde allerdings klar, dass die Förderung der deutsch-italienischen Freundschaft keine ausschließliche Sache von Italienern einerseits und Deutschen andererseits ist.'
                    : 'Ben presto è diventato chiaro che la promozione dell\'amicizia italo-tedesca non è una questione esclusiva di italiani da una parte e tedeschi dall\'altra.'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Wir stehen für ein vereintes Europa, in dem selbstverständlich sein sollte, dass Menschen unterschiedlicher Nationalitäten sich dafür engagieren. Denn wir glauben fest daran, dass alle Menschen – unabhängig von ihrer Herkunft – aktiv dazu beitragen, eine wahre europäische Gemeinschaft zu formen, die auf gemeinsamen Werten und einer geteilten Kultur basiert, jenseits von politischen und wirtschaftlichen Grenzen.'
                    : 'Sosteniamo un\'Europa unita, in cui dovrebbe essere naturale che persone di diverse nazionalità si impegnino. Crediamo fermamente che tutte le persone – indipendentemente dalla loro origine – contribuiscano attivamente a formare una vera comunità europea, basata su valori comuni e una cultura condivisa, al di là dei confini politici ed economici.'}
                </p>
                <p className="font-semibold text-foreground">
                  {language === 'de'
                    ? 'Amitalia e.V. setzt sich für Frieden, Demokratie, Toleranz und Freiheit ein.'
                    : 'Amitalia e.V. si impegna per la pace, la democrazia, la tolleranza e la libertà.'}
                </p>
                <p>
                  {language === 'de'
                    ? 'Mit unserem Wirken möchten wir genau dafür Räume und Gelegenheiten schaffen.'
                    : 'Con il nostro lavoro vogliamo creare spazi e opportunità proprio per questo.'}
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="w-full aspect-[4/3] bg-gradient-to-br from-italian-green/20 via-white to-ferrari/20 luxury-shadow flex items-center justify-center">
                <div className="text-center">
                  <span className="font-heading text-6xl font-semibold text-foreground/10">DE + IT</span>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-ferrari text-white p-6">
                <div className="font-accent text-sm">2011 - {new Date().getFullYear()}</div>
                <div className="font-heading text-2xl">14+ {language === 'de' ? 'Jahre' : 'Anni'}</div>
              </div>
            </motion.div>
          </div>

          {/* Activities List */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 bg-[#F5F5F7] p-8 md:p-12"
          >
            <h3 className="font-heading text-2xl font-semibold mb-6">
              {language === 'de' ? 'Amitalia e.V. organisiert:' : 'Amitalia e.V. organizza:'}
            </h3>
            <ul className="grid md:grid-cols-2 gap-4 text-foreground/70">
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-ferrari rounded-full"></span>
                {language === 'de' ? 'Kulturelle Veranstaltungen' : 'Eventi culturali'}
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-ferrari rounded-full"></span>
                {language === 'de' ? 'Lesungen und Vorträge' : 'Letture e conferenze'}
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-ferrari rounded-full"></span>
                {language === 'de' ? 'Musikalische Veranstaltungen' : 'Eventi musicali'}
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-ferrari rounded-full"></span>
                {language === 'de' ? 'Ausflüge in der Umgebung' : 'Escursioni nei dintorni'}
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-ferrari rounded-full"></span>
                {language === 'de' ? 'Museumsbesuche' : 'Visite ai musei'}
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-ferrari rounded-full"></span>
                {language === 'de' ? 'Kulinarische Veranstaltungen' : 'Eventi culinari'}
              </li>
            </ul>
            <p className="mt-8 text-foreground/70 border-t border-black/10 pt-6">
              {language === 'de'
                ? 'Regelmäßig, mindestens einmal im Monat, treffen sich die Mitglieder von Amitalia e. V. und diskutieren über aktuelle Themen der Politik, insbesondere im Zusammenhang mit den politischen Ereignissen in Deutschland und Italien. Ort und Uhrzeit variieren und werden auf der Homepage veröffentlicht.'
                : 'Regolarmente, almeno una volta al mese, i membri di Amitalia e.V. si incontrano e discutono di temi politici attuali, in particolare in relazione agli eventi politici in Germania e Italia. Luogo e orario variano e vengono pubblicati sul sito web.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#F5F5F7]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              {t('about_values_title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 luxury-shadow"
                data-testid={`value-card-${index}`}
              >
                <value.icon className="w-10 h-10 text-ferrari mb-6" />
                <h3 className="font-heading text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              {t('about_team_title')}
            </h2>
            <p className="text-foreground/60 max-w-2xl mx-auto">
              {t('about_team_text')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`team-member-${index}`}
              >
                <div className="w-32 h-32 mx-auto mb-4 overflow-hidden rounded-full border-4 border-white luxury-shadow bg-[#F5F5F7] flex items-center justify-center">
                  <span className="font-heading text-3xl font-semibold text-foreground/40">{member.initials}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold">{member.name}</h3>
                <p className="text-sm text-foreground/60">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Statutes Section */}
      <section className="py-20 bg-[#1A1A1A] text-white" data-testid="statutes-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Users className="w-12 h-12 mx-auto mb-6 text-german-gold" />
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
            {language === 'de' ? 'Vereinssatzung' : 'Statuto'}
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-8">
            {language === 'de'
              ? 'Unsere Satzung bildet das Fundament unseres gemeinnützigen Vereins und regelt die Organisation, Ziele und Mitgliedschaft.'
              : 'Il nostro statuto costituisce il fondamento della nostra associazione no-profit e regola l\'organizzazione, gli obiettivi e l\'adesione.'}
          </p>
          <button className="btn-secondary border-white text-white hover:bg-white hover:text-foreground">
            {language === 'de' ? 'Satzung herunterladen' : 'Scarica lo statuto'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default About;
